/**
 * Job History Modal Component
 *
 * Displays a modal showing the complete history of PRD breakdown jobs for a project.
 * Useful for tracking long-running jobs and reviewing past results after navigating away.
 */

import type { JSX } from 'react';
import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@heroui/react';
import type { ChipProps } from '@heroui/react';
import { useQuery } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlertCircle,
  BanIcon,
  Bot,
  CheckCircle2,
  Clock,
  FileText,
  History,
  ListTodo,
  UserPlus,
  XCircle,
} from 'lucide-react';

import type { JobHistoryItem } from '~/services/api/prd';
import { getBreakdownHistory } from '~/services/api/prd';
import { formatDuration, formatRelativeTime } from '~/utils/formatDuration';
import { logger } from '~/utils/logger';

interface JobHistoryModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  hasTasks?: boolean;
  totalTasks?: number;
  completedTasks?: number;
}

interface StatusConfig {
  color: ChipProps['color'];
  icon: LucideIcon;
  label: string;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  PENDING: {
    color: 'warning',
    icon: Clock,
    label: 'Pending',
  },
  PROCESSING: {
    color: 'primary',
    icon: Activity,
    label: 'Processing',
  },
  COMPLETED: {
    color: 'success',
    icon: CheckCircle2,
    label: 'Completed',
  },
  FAILED: {
    color: 'danger',
    icon: XCircle,
    label: 'Failed',
  },
  CANCELLED: {
    color: 'default',
    icon: BanIcon,
    label: 'Cancelled',
  },
};

/**
 * Format phase name into user-friendly activity description.
 */
function formatPhaseName(phase: string | null): string {
  if (!phase) return '';

  const phaseNames: Record<string, string> = {
    prd_analysis: 'Analyzing PRD',
    task_generation: 'Creating tasks',
    backend_enrichment: 'Backend tasks',
    frontend_enrichment: 'Frontend tasks',
    infrastructure_enrichment: 'Infrastructure tasks',
    database_enrichment: 'Database tasks',
    testing_enrichment: 'Test tasks',
    documentation_enrichment: 'Documentation tasks',
    validation: 'Validation',
    task_creation: 'Saving tasks',
    completed: 'Complete',
  };

  return phaseNames[phase] ?? phase.replace(/_/g, ' ');
}

function JobHistoryRow({ job }: { job: JobHistoryItem }): JSX.Element {
  const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG['PENDING'];
  const StatusIcon = statusConfig.icon;

  // Calculate duration (time taken to complete)
  let duration: string | null = null;
  if (job.started_at && job.completed_at) {
    const durationMs = new Date(job.completed_at).getTime() - new Date(job.started_at).getTime();
    const durationSeconds = Math.floor(durationMs / 1000);
    duration = formatDuration(durationSeconds);
  }

  // Calculate when job started (time ago)
  const startedAgo = job.started_at
    ? formatRelativeTime(Math.floor((Date.now() - new Date(job.started_at).getTime()) / 1000))
    : null;

  // Calculate elapsed time if still processing
  const isProcessing = job.status === 'PROCESSING';
  const elapsedTime =
    isProcessing && job.started_at
      ? formatDuration(Math.floor((Date.now() - new Date(job.started_at).getTime()) / 1000))
      : null;

  return (
    <div className="flex flex-col gap-2 p-3 rounded-lg bg-surface-elevated dark:bg-surface/50 hover:bg-surface dark:hover:bg-surface border border-divider transition-colors">
      {/* Header Row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Chip
            size="sm"
            color={statusConfig.color}
            variant="flat"
            startContent={<StatusIcon className="w-3 h-3" />}
          >
            {statusConfig.label}
          </Chip>
          <span className="flex items-center gap-1 text-xs text-foreground-600 dark:text-muted">
            <Bot className="w-3 h-3" />
            PRD Breakdown
          </span>
          {job.current_phase && (
            <span className="text-xs text-foreground-500/60 dark:text-muted/60 truncate">
              {formatPhaseName(job.current_phase)}
            </span>
          )}
        </div>
        <div className="text-xs text-foreground-600 dark:text-muted shrink-0">
          {new Date(job.created_at).toLocaleString()}
        </div>
      </div>

      {/* Metadata Row: Task Count, Tech Spec, Duration, Started */}
      <div className="flex items-center gap-3 flex-wrap text-sm">
        {job.tasks_created_count !== null && job.tasks_created_count !== undefined && (
          <div className="flex items-center gap-1.5 text-foreground-600 dark:text-muted">
            <ListTodo className="w-3.5 h-3.5" />
            <span>{job.tasks_created_count} tasks</span>
          </div>
        )}
        {job.has_tech_spec && (
          <div className="flex items-center gap-1.5 text-foreground-600 dark:text-muted">
            <FileText className="w-3.5 h-3.5" />
            <span>Tech Spec</span>
          </div>
        )}
        {duration && (
          <div className="flex items-center gap-1.5 text-foreground-600 dark:text-muted">
            <Clock className="w-3.5 h-3.5" />
            <span>Duration: {duration}</span>
          </div>
        )}
        {startedAgo && !isProcessing && (
          <div className="flex items-center gap-1.5 text-foreground-600 dark:text-muted">
            <Activity className="w-3.5 h-3.5" />
            <span>Started {startedAgo}</span>
          </div>
        )}
      </div>

      {/* Duration/Time Info (Processing) */}
      {isProcessing && elapsedTime && (
        <div className="text-sm text-primary dark:text-primary">Running: {elapsedTime}</div>
      )}

      {/* Error Message */}
      {job.error_message && (
        <div className="flex items-start gap-2 p-2 rounded bg-danger/10 dark:bg-danger/20 border-1 border-danger/20 dark:border-danger/30">
          <AlertCircle className="w-4 h-4 text-danger shrink-0 mt-0.5" />
          <div className="text-xs text-danger dark:text-danger">{job.error_message}</div>
        </div>
      )}

      {/* Job ID (truncated) */}
      <div className="text-xs font-mono text-foreground-500/60 dark:text-muted/60 truncate">
        ID: {job.job_id}
      </div>
    </div>
  );
}

export function JobHistoryModal({
  completedTasks,
  hasTasks,
  isOpen,
  onClose,
  projectId,
  totalTasks,
}: JobHistoryModalProps): JSX.Element {
  const {
    data: history,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['prd-breakdown-history', projectId],
    queryFn: () => getBreakdownHistory(projectId, 10),
    enabled: isOpen, // Only fetch when modal is open
    staleTime: 30000, // 30 seconds
  });

  const handleClose = (): void => {
    logger.info({ component: 'JobHistoryModal', projectId }, 'User closed job history modal');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="2xl"
      scrollBehavior="inside"
      backdrop="blur"
      classNames={{
        base: 'bg-surface-elevated dark:bg-surface-elevated',
        header: 'bg-surface-elevated dark:bg-surface-elevated border-b-1 border-divider',
        body: 'bg-surface-elevated dark:bg-surface-elevated',
        footer: 'bg-surface-elevated dark:bg-surface-elevated border-t-1 border-divider',
      }}
    >
      <ModalContent className="bg-surface-elevated dark:bg-surface-elevated">
        <ModalHeader className="flex items-center gap-2 text-foreground">
          <History className="w-5 h-5" />
          <span>Breakdown Job History</span>
        </ModalHeader>

        <ModalBody className="py-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-4 rounded-lg bg-danger/10 dark:bg-danger/20 border-1 border-danger/20 dark:border-danger/30">
              <AlertCircle className="w-5 h-5 text-danger shrink-0 mt-0.5" />
              <div className="flex-1 text-sm text-danger dark:text-danger">
                <div className="font-semibold mb-1">Failed to load history</div>
                <div>{error instanceof Error ? error.message : 'Unknown error occurred'}</div>
              </div>
            </div>
          )}

          {!isLoading && !error && history && (
            <>
              {history.jobs.length === 0 ? (
                hasTasks ? (
                  <div className="space-y-2">
                    <div className="flex flex-col gap-2 p-3 rounded-lg bg-surface-elevated dark:bg-surface/50 border border-divider/50 border-dashed">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <Chip
                            size="sm"
                            color="default"
                            variant="flat"
                            startContent={<UserPlus className="w-3 h-3" />}
                          >
                            Manual
                          </Chip>
                          <span className="text-xs text-foreground-600 dark:text-muted">
                            Tasks added by agent
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap text-sm">
                        <div className="flex items-center gap-1.5 text-foreground-600 dark:text-muted">
                          <ListTodo className="w-3.5 h-3.5" />
                          <span>{totalTasks ?? 0} tasks</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-foreground-600 dark:text-muted">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>{completedTasks ?? 0} completed</span>
                        </div>
                      </div>
                      <p className="text-xs text-foreground-500/60 dark:text-muted/60">
                        These tasks were not generated from a PRD breakdown
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <History className="w-12 h-12 text-foreground-500/30 dark:text-muted/30 mb-3" />
                    <p className="text-foreground-600 dark:text-muted">
                      No breakdown jobs found for this project
                    </p>
                    <p className="text-sm text-foreground-500/60 dark:text-muted/60 mt-1">
                      Job history will appear here after generating tasks from a PRD
                    </p>
                  </div>
                )
              ) : (
                <div className="space-y-2">
                  {history.jobs.map(job => (
                    <JobHistoryRow key={job.job_id} job={job} />
                  ))}
                </div>
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="primary" onPress={handleClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
